import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as THREE from 'three';
import {
    CSS2DRenderer,
    CSS2DObject,
} from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import CameraControls from 'camera-controls';
import SceneSelector from './SceneSelector';
import HotspotForm from './HotspotForm';
import HotspotList from './HotspotList';
import {
    getTourConfig,
    saveTourConfig,
    addInfoSpot,
    updateInfoSpot,
    deleteInfoSpot,
    addLink,
    updateLink,
    deleteLink,
} from '../../admin/adminPanoConfig';
import './style.css';

// Same hotspot icons used in the public viewer
import tagPNG from '../../assets/images/tag.svg';
import mapIconWhite from '../../assets/svg/map-icon-white.svg';
import mapIconYellow from '../../assets/svg/map-icon-yellow1.svg';
import mapCircle from '../../assets/svg/map-icon-line-white.svg';
import infoInnerWhite from '../../assets/svg/hotspot-inner-white.svg';
import infoOuterWhite from '../../assets/svg/hotspot-outer-white.svg';
import infoInnerYellow from '../../assets/svg/hotspot-inner-yellow.svg';

// Import same panoView styles for hotspot animations
import '../panoView/style.css';

CameraControls.install({ THREE });

const geometry = new THREE.SphereGeometry(500, 60, 40);

function AdminEditor() {
    const history = useHistory();
    const containerRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const renderer2Ref = useRef(null);
    const scene2Ref = useRef(null);
    const controlsRef = useRef(null);
    const clockRef = useRef(null);
    const animFrameRef = useRef(null);
    const markerObjectsRef = useRef([]);
    const isPlacingRef = useRef(false);

    const [currentScene, setCurrentScene] = useState(0);
    const [placingType, setPlacingType] = useState(null); // 'info' | 'transition' | null
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('info');
    const [clickPosition, setClickPosition] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [tourMeta, setTourMeta] = useState(null);
    const [config, setConfig] = useState(null);
    const [initialized, setInitialized] = useState(false);
    const [sceneAudioFilesMale, setSceneAudioFilesMale] = useState([]);
    const [sceneAudioFilesFemale, setSceneAudioFilesFemale] = useState([]);

    const { tourId } = useParams();

    // Auth check
    useEffect(() => {
        if (sessionStorage.getItem('adminLoggedIn') !== 'true') {
            history.push('/admin/login');
        }
    }, [history]);

    // Load config
    useEffect(() => {
        async function fetchConfig() {
            if (!tourId) return;
            const data = await getTourConfig(tourId);
            if (data) {
                setTourMeta(data.metadata);
                setConfig(data.config);
            } else {
                alert('Tour not found!');
                history.push('/admin/tours');
            }
        }
        fetchConfig();
    }, [tourId, history]);

    // Fetch audio files from the tour's main VO folders when tour loads
    useEffect(() => {
        if (!tourMeta) {
            setSceneAudioFilesMale([]);
            setSceneAudioFilesFemale([]);
            return;
        }

        if (tourMeta.mainVoFolderMale) {
            fetch(`http://localhost:3002/api/audio-files?folder=${encodeURIComponent(tourMeta.mainVoFolderMale)}`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) setSceneAudioFilesMale(data);
                })
                .catch(err => console.error('Failed to load Male VO files:', err));
        }

        if (tourMeta.mainVoFolderFemale) {
            fetch(`http://localhost:3002/api/audio-files?folder=${encodeURIComponent(tourMeta.mainVoFolderFemale)}`)
                .then(res => res.json())
                .then(data => {
                    if (Array.isArray(data)) setSceneAudioFilesFemale(data);
                })
                .catch(err => console.error('Failed to load Female VO files:', err));
        }
    }, [tourMeta]);

    // Init Three.js
    useEffect(() => {
        if (!containerRef.current || initialized) return;

        const container = containerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        // Camera
        const camera = new THREE.PerspectiveCamera(60, width / height, 1, 10000);
        camera.position.z = 0.01;
        camera.fov = THREE.MathUtils.clamp(100, 10, 75);
        camera.updateProjectionMatrix();
        cameraRef.current = camera;

        // Scene for panorama
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        // Renderer
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // CSS2D Renderer for hotspot markers
        const scene2 = new THREE.Scene();
        scene2Ref.current = scene2;
        const renderer2 = new CSS2DRenderer();
        renderer2.setSize(width, height);
        renderer2.domElement.style.position = 'absolute';
        renderer2.domElement.style.top = '0';
        renderer2.domElement.style.left = '0';
        renderer2.domElement.style.pointerEvents = 'none';
        container.appendChild(renderer2.domElement);
        renderer2Ref.current = renderer2;

        // Camera controls
        const clock = new THREE.Clock();
        clockRef.current = clock;
        const controls = new CameraControls(camera, renderer.domElement);
        controls.minDistance = 0.01;
        controls.maxDistance = 0.01;
        controls.dollySpeed = 0;
        controls.truckSpeed = 0;
        controls.azimuthRotateSpeed = 0.5;
        controls.polarRotateSpeed = 0.5;
        controlsRef.current = controls;

        // Animation loop
        const animate = () => {
            animFrameRef.current = requestAnimationFrame(animate);
            const delta = clock.getDelta();
            controls.update(delta);
            renderer.render(scene, camera);
            renderer2.render(scene2, camera);
        };
        animate();

        // Resize handler
        const handleResize = () => {
            const w = container.clientWidth;
            const h = container.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
            renderer2.setSize(w, h);
        };
        window.addEventListener('resize', handleResize);

        setInitialized(true);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (renderer.domElement && container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
            if (renderer2.domElement && container.contains(renderer2.domElement)) {
                container.removeChild(renderer2.domElement);
            }
            renderer.dispose();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load panorama image for current scene
    const loadPanoImage = useCallback((sceneIndex) => {
        const scene = sceneRef.current;
        if (!scene || !config) return;

        // Clear existing meshes
        while (scene.children.length > 0) {
            scene.remove(scene.children[0]);
        }

        const view = config[sceneIndex];
        if (!view) return;

        const loader = new THREE.TextureLoader();
        loader.load(view.image, (texture) => {
            texture.mapping = THREE.EquirectangularReflectionMapping;
            const material = new THREE.MeshBasicMaterial({
                map: texture,
                side: THREE.BackSide
            });
            const mesh = new THREE.Mesh(geometry, material);
            // Flip the sphere so raycasting works on the inside
            mesh.scale.x = -1;
            scene.add(mesh);
        });

        // Reset camera
        if (controlsRef.current) {
            controlsRef.current.reset(false);
            const rot = view.initialRotation || [90, 0];
            controlsRef.current.rotate(
                rot[0] * THREE.MathUtils.DEG2RAD,
                rot[1] * THREE.MathUtils.DEG2RAD,
                false
            );
        }
    }, [config]);

    // Load scene when changed or config initially loads
    useEffect(() => {
        if (initialized && config) {
            loadPanoImage(currentScene);
        }
    }, [currentScene, initialized, config, loadPanoImage]);

    // Render markers for existing hotspots
    const renderMarkers = useCallback(() => {
        const scene2 = scene2Ref.current;
        if (!scene2 || !config) return;

        // Clear old markers
        markerObjectsRef.current.forEach((obj) => scene2.remove(obj));
        markerObjectsRef.current = [];

        const sceneConfig = config[currentScene];
        if (!sceneConfig) return;

        // Info spot markers — same visual as public viewer's addInfoPoint
        sceneConfig.infoSpots.forEach((spot, idx) => {
            const infoSpot = document.createElement('div');
            infoSpot.className = 'info-spot-wrap hotspot';
            infoSpot.style.pointerEvents = 'auto';
            infoSpot.style.cursor = 'pointer';

            // Animated inner circle
            const animInner = document.createElement('img');
            animInner.style.zIndex = -50;
            animInner.className = 'infospot-inner-circle';
            animInner.src = infoInnerWhite;
            infoSpot.appendChild(animInner);

            // Animated outer circle
            const animOuter = document.createElement('img');
            animOuter.className = 'infospot-outer-circle';
            animOuter.src = infoOuterWhite;
            infoSpot.appendChild(animOuter);

            // Hover states (hidden by default)
            const animHoverInnerWhite = document.createElement('img');
            animHoverInnerWhite.className = 'infospot-hover-inner-circle-white hide';
            animHoverInnerWhite.src = infoInnerWhite;
            infoSpot.appendChild(animHoverInnerWhite);

            const animHoverInnerYellow = document.createElement('img');
            animHoverInnerYellow.className = 'infospot-hover-inner-circle-yellow hide';
            animHoverInnerYellow.src = infoInnerYellow;
            infoSpot.appendChild(animHoverInnerYellow);

            const animHoverOuter = document.createElement('img');
            animHoverOuter.className = 'infospot-hover-outer-circle hide';
            animHoverOuter.src = infoOuterWhite;
            infoSpot.appendChild(animHoverOuter);

            // Name plate
            const namePlate = document.createElement('div');
            namePlate.className = 'name-plate';
            namePlate.style.pointerEvents = 'none';
            const namePlateTag1 = document.createElement('div');
            namePlateTag1.className = 'name-plate-tag1';
            const text = document.createElement('p');
            text.style.paddingTop = '0px';
            text.style.paddingBottom = '0px';
            text.innerHTML = spot.solutionModelData?.infoSpot || 'Info';
            namePlateTag1.appendChild(text);
            const namePlateTag = document.createElement('img');
            namePlateTag.src = tagPNG;
            namePlateTag.className = 'name-plate-tag';
            namePlate.appendChild(namePlateTag1);
            namePlate.appendChild(namePlateTag);
            infoSpot.appendChild(namePlate);

            // Hover effects
            infoSpot.onmouseenter = () => {
                animInner.style.display = 'none';
                animOuter.style.display = 'none';
                animHoverInnerWhite.style.display = 'block';
                animHoverInnerYellow.style.display = 'block';
                animHoverOuter.style.display = 'block';
                namePlate.style.opacity = '1';
                namePlate.style.display = 'block';
            };
            infoSpot.onmouseleave = () => {
                animInner.style.display = 'block';
                animOuter.style.display = 'block';
                animHoverInnerWhite.style.display = 'none';
                animHoverInnerYellow.style.display = 'none';
                animHoverOuter.style.display = 'none';
                namePlate.style.opacity = '0';
                namePlate.style.display = 'none';
            };

            infoSpot.onclick = (e) => {
                e.stopPropagation();
                handleEditInfoSpot(spot, idx);
            };

            const obj = new CSS2DObject(infoSpot);
            obj.position.set(spot.position.x, spot.position.y, spot.position.z);
            scene2.add(obj);
            markerObjectsRef.current.push(obj);
        });

        // Link markers — same visual as public viewer's addLinkPoint
        sceneConfig.links.forEach((link, idx) => {
            const hotSpot = document.createElement('div');
            hotSpot.className = 'hotspot';
            hotSpot.style.pointerEvents = 'auto';
            hotSpot.style.cursor = 'pointer';

            // Name plate
            const namePlate = document.createElement('div');
            namePlate.className = 'name-plate';
            const namePlateTag1 = document.createElement('div');
            namePlateTag1.className = 'name-plate-tag1';
            const text = document.createElement('p');
            text.style.paddingTop = '0px';
            text.style.paddingBottom = '0px';
            text.innerHTML = link.name || 'Link';
            namePlateTag1.appendChild(text);
            const namePlateTag = document.createElement('img');
            namePlateTag.src = tagPNG;
            namePlateTag.className = 'name-plate-tag';
            namePlate.appendChild(namePlateTag1);
            namePlate.appendChild(namePlateTag);
            hotSpot.appendChild(namePlate);

            // Map icon (white)
            const anim = document.createElement('img');
            anim.className = 'vert-move-white';
            anim.src = mapIconWhite;
            hotSpot.appendChild(anim);

            // Map icon hover (yellow)
            const animHover = document.createElement('img');
            animHover.className = 'vert-move-yellow hotspot hide';
            animHover.src = mapIconYellow;
            hotSpot.appendChild(animHover);

            // Map circle
            const animHoverCircle = document.createElement('img');
            animHoverCircle.className = 'map-circle hide';
            animHoverCircle.src = mapCircle;
            hotSpot.appendChild(animHoverCircle);

            // Hover effects
            hotSpot.onmouseenter = () => {
                anim.style.display = 'none';
                animHover.style.display = 'block';
                animHoverCircle.style.display = 'block';
                namePlate.style.opacity = '1';
                namePlate.style.display = 'block';
            };
            hotSpot.onmouseleave = () => {
                anim.style.display = 'block';
                animHover.style.display = 'none';
                animHoverCircle.style.display = 'none';
                namePlate.style.opacity = '0';
                namePlate.style.display = 'none';
            };

            hotSpot.onclick = (e) => {
                e.stopPropagation();
                handleEditLink(link, idx);
            };

            const obj = new CSS2DObject(hotSpot);
            obj.position.set(link.position.x, link.position.y, link.position.z);
            scene2.add(obj);
            markerObjectsRef.current.push(obj);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, currentScene]);

    useEffect(() => {
        if (initialized) {
            renderMarkers();
        }
    }, [initialized, renderMarkers]);

    // Click-to-place handler
    const handlePanoClick = useCallback((e) => {
        if (!placingType || !containerRef.current || !cameraRef.current) return;

        const container = containerRef.current;
        const rect = container.getBoundingClientRect();
        const mouse = new THREE.Vector2(
            ((e.clientX - rect.left) / rect.width) * 2 - 1,
            -((e.clientY - rect.top) / rect.height) * 2 + 1
        );

        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, cameraRef.current);

        // Intersect with the panorama sphere
        const scene = sceneRef.current;
        if (!scene) return;

        const meshes = [];
        scene.traverse((child) => {
            if (child.isMesh) meshes.push(child);
        });

        const intersects = raycaster.intersectObjects(meshes);
        if (intersects.length > 0) {
            const point = intersects[0].point;
            // Normalize to a radius of ~200 for consistency with existing data
            const normalized = point.normalize().multiplyScalar(200);

            setClickPosition({ x: normalized.x, y: normalized.y, z: normalized.z });
            setFormType(placingType);
            setEditingItem(null);
            setShowForm(true);
            setPlacingType(null);
            isPlacingRef.current = false;
        }
    }, [placingType]);

    // Scene change handler
    const handleSceneChange = (idx) => {
        setCurrentScene(idx);
        setShowForm(false);
        setPlacingType(null);
        setEditingItem(null);
    };

    // Save info spot
    const handleSaveInfoSpot = async (data) => {
        if (editingItem) {
            await updateInfoSpot(tourId, currentScene, editingItem.id, {
                name: data.name,
                position: data.position,
                solutionModelData: {
                    infoSpot: data.name,
                    infoSpotType: data.infoSpotType,
                    audioFolder: data.audioFolder,
                    audioFile: data.audioFile,
                    audioFileFemale: data.audioFileFemale,
                    title: data.title,
                    primaryContent: data.primaryContent,
                    hotspotType: data.hotspotType,
                    hotspotRoom: data.hotspotRoom,
                    hotspotProduct: data.hotspotProduct,
                    breadCrum: data.breadCrum,
                    productHeadline: data.productHeadline,
                    primaryImage: data.primaryImage,
                    icon: data.icon,
                    filterLabels: data.filterLabels,
                },
            });
        } else {
            await addInfoSpot(tourId, currentScene, data);
        }
        const newData = await getTourConfig(tourId);
        setConfig(newData.config);
        setShowForm(false);
        setEditingItem(null);
        setClickPosition(null);
    };

    // Save link
    const handleSaveLink = async (data) => {
        if (editingItem) {
            await updateLink(tourId, currentScene, editingItem.id, {
                name: data.name,
                linkTo: data.linkTo,
                hotspotRoom: data.hotspotRoom,
                position: data.position,
            });
        } else {
            await addLink(tourId, currentScene, data);
        }
        const newData = await getTourConfig(tourId);
        setConfig(newData.config);
        setShowForm(false);
        setEditingItem(null);
        setClickPosition(null);
    };

    // Edit handlers
    const handleEditInfoSpot = (spot, idx) => {
        setEditingItem(spot);
        setFormType('info');
        setClickPosition(spot.position);
        setShowForm(true);
    };

    const handleEditLink = (link, idx) => {
        setEditingItem(link);
        setFormType('transition');
        setClickPosition(link.position);
        setShowForm(true);
    };

    // Delete handlers
    const handleDeleteInfoSpot = async (spotId) => {
        if (!window.confirm('Delete this info hotspot?')) return;
        await deleteInfoSpot(tourId, currentScene, spotId);
        const newData = await getTourConfig(tourId);
        setConfig(newData.config);
    };

    const handleDeleteLink = async (linkId) => {
        if (!window.confirm('Delete this transition node?')) return;
        await deleteLink(tourId, currentScene, linkId);
        const newData = await getTourConfig(tourId);
        setConfig(newData.config);
    };

    // Toggle placing mode
    const startPlacing = (type) => {
        setPlacingType(type);
        isPlacingRef.current = true;
        setShowForm(false);
        setEditingItem(null);
    };

    // Logout
    const handleLogout = () => {
        sessionStorage.removeItem('adminLoggedIn');
        history.push('/admin/login');
    };

    const handleBackToTours = () => {
        history.push('/admin/tours');
    };

    const currentConfig = config ? config[currentScene] : null;

    // Handle scene audio clip selection
    const handleSceneAudioChange = async (audioFile, gender = 'male') => {
        if (!config) return;
        const updated = [...config];
        if (gender === 'female') {
            updated[currentScene] = { ...updated[currentScene], sceneAudioFemale: audioFile };
        } else {
            updated[currentScene] = { ...updated[currentScene], sceneAudio: audioFile };
        }
        setConfig(updated);
        await saveTourConfig(tourId, updated);
    };

    return (
        <div className="admin-editor-container">
            {/* Top Bar */}
            <div className="admin-topbar">
                <div className="admin-topbar-left">
                    <button className="admin-topbar-btn" onClick={handleBackToTours} style={{ marginRight: '15px' }}>
                        ← Tours
                    </button>
                    <span className="admin-topbar-title">🔧 Editor: {tourMeta ? tourMeta.name : 'Loading...'}</span>
                    <SceneSelector
                        currentScene={currentScene}
                        scenes={config}
                        onSceneChange={handleSceneChange}
                    />
                </div>
                <div className="admin-topbar-right">
                    <button className="admin-topbar-btn" onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </div>

            <div className="admin-editor-body">
                {/* Panorama Viewer */}
                <div className="admin-pano-wrapper">
                    <div
                        className={`admin-pano-container ${placingType ? 'admin-placing-mode' : ''}`}
                        ref={containerRef}
                        onClick={handlePanoClick}
                    />
                    {placingType && (
                        <div className="admin-placing-banner">
                            🎯 Click on the panorama to place a {placingType === 'info' ? 'info hotspot' : 'transition node'}
                            <button onClick={() => { setPlacingType(null); isPlacingRef.current = false; }}>Cancel</button>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="admin-sidebar">
                    <div className="admin-toolbar">
                        <h4>Toolbar</h4>
                        <button
                            className={`admin-tool-btn admin-tool-info ${placingType === 'info' ? 'active' : ''}`}
                            onClick={() => startPlacing('info')}
                        >
                            📌 Place Info Hotspot
                        </button>
                        <button
                            className={`admin-tool-btn admin-tool-link ${placingType === 'transition' ? 'active' : ''}`}
                            onClick={() => startPlacing('transition')}
                        >
                            🔗 Place Transition Node
                        </button>

                        <hr style={{ margin: '20px 0', borderColor: 'rgba(255,255,255,0.1)' }} />

                        {/* Scene Audio Clip */}
                        {(tourMeta?.mainVoFolderMale || tourMeta?.mainVoFolderFemale) && (
                            <div className="admin-scene-audio" style={{ padding: '0', background: 'transparent', border: 'none' }}>
                                <h4 style={{ marginBottom: '15px' }}>🔊 Scene Audio (VO)</h4>
                            
                            <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '8px', display: 'block' }}>👨 Male VO</label>
                            <input
                                list="scene-audio-files-male"
                                value={currentConfig?.sceneAudio || ''}
                                onChange={(e) => handleSceneAudioChange(e.target.value, 'male')}
                                placeholder="Search male audio..."
                                style={{ marginBottom: '8px' }}
                                autoComplete="off"
                            />
                            {currentConfig?.sceneAudio && (
                                <p className="admin-scene-audio-path">
                                    📁 {tourMeta.mainVoFolderMale}/{currentConfig.sceneAudio}.mp3
                                </p>
                            )}
                            <datalist id="scene-audio-files-male">
                                {sceneAudioFilesMale.map(file => (
                                    <option key={file} value={file} />
                                ))}
                            </datalist>

                            <label style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '12px', display: 'block' }}>👩 Female VO</label>
                            <input
                                list="scene-audio-files-female"
                                value={currentConfig?.sceneAudioFemale || ''}
                                onChange={(e) => handleSceneAudioChange(e.target.value, 'female')}
                                placeholder="Search female audio..."
                                autoComplete="off"
                            />
                            {currentConfig?.sceneAudioFemale && (
                                <p className="admin-scene-audio-path">
                                    📁 {tourMeta.mainVoFolderFemale}/{currentConfig.sceneAudioFemale}.mp3
                                </p>
                            )}
                            <datalist id="scene-audio-files-female">
                                {sceneAudioFilesFemale.map(file => (
                                    <option key={file} value={file} />
                                ))}
                            </datalist>
                        </div>
                    )}
                    </div>

                    <HotspotList
                        infoSpots={currentConfig?.infoSpots || []}
                        links={currentConfig?.links || []}
                        scenes={config}
                        onEditInfoSpot={handleEditInfoSpot}
                        onDeleteInfoSpot={handleDeleteInfoSpot}
                        onEditLink={handleEditLink}
                        onDeleteLink={handleDeleteLink}
                    />
                </div>
            </div>

            {/* Form Overlay */}
            {showForm && (
                <HotspotForm
                    type={formType}
                    initialData={editingItem}
                    position={clickPosition}
                    scenes={config}
                    metadata={tourMeta}
                    onSave={formType === 'info' ? handleSaveInfoSpot : handleSaveLink}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingItem(null);
                        setClickPosition(null);
                    }}
                />
            )}
        </div>
    );
}

export default AdminEditor;
